/**
 * Single source of truth for the HLC brand theme.
 *
 * Values are drawn from the colors already established elsewhere in this
 * codebase (the original calculator's brand gradient, the Top Selling
 * Products / Why Choose HLC sections) rather than invented — the intent
 * is one file to edit when the site's look needs to change, not a second
 * competing palette.
 *
 * `cssVars` flattens `theme` into CSS custom properties. Apply it as an
 * inline `style` on a component's root element (e.g.
 * `<section style={cssVars}>`) and the rest of that component's CSS can
 * reference `var(--hlc-*)` — changing a value here propagates everywhere
 * without touching component stylesheets.
 */

export const theme = {
  colors: {
    background: "#0b0710",
    backgroundSecondary: "#150c1c",
    surface: "rgba(255, 255, 255, 0.035)",
    surfaceElevated: "rgba(255, 255, 255, 0.07)",
    purple: "#542757",
    purpleDeep: "#35335b",
    magenta: "#9b1f6c",
    magentaLight: "#e0468f",
    gold: "#ffd55a",
    goldDeep: "#ffb13b",
    white: "#ffffff",
    textPrimary: "#f7f4fb",
    textSecondary: "#a99cbd",
    textFaint: "#7d7191",
    border: "rgba(255, 255, 255, 0.10)",
    borderStrong: "rgba(255, 255, 255, 0.20)",
    success: "#6bd18a",
  },

  gradients: {
    background:
      "radial-gradient(60% 50% at 18% 8%, rgba(155,31,108,0.24), transparent 60%), " +
      "radial-gradient(55% 45% at 85% 15%, rgba(84,39,87,0.28), transparent 60%), " +
      "radial-gradient(70% 60% at 50% 100%, rgba(155,31,108,0.16), transparent 65%), " +
      "linear-gradient(180deg, #0b0710 0%, #150c1c 45%, #0a0812 100%)",
    primary: "linear-gradient(120deg, #542757, #9b1f6c)",
    savings: "linear-gradient(120deg, #e0468f, #ffd55a)",
    glow: "radial-gradient(circle at 38% 32%, rgba(155,31,108,0.4), rgba(9,7,16,0.85) 62%)",
  },

  shadows: {
    card: "0 20px 46px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.06)",
    glow: "0 0 46px rgba(155,31,108,0.32)",
    strongGlow: "0 0 56px rgba(155,31,108,0.4), 0 0 84px rgba(255,213,90,0.10)",
  },

  effects: {
    blur: "20px",
    borderRadius: "18px",
    borderRadiusSm: "12px",
  },
};

export const cssVars = {
  "--hlc-bg": theme.colors.background,
  "--hlc-bg-secondary": theme.colors.backgroundSecondary,
  "--hlc-surface": theme.colors.surface,
  "--hlc-surface-elevated": theme.colors.surfaceElevated,
  "--hlc-purple": theme.colors.purple,
  "--hlc-purple-deep": theme.colors.purpleDeep,
  "--hlc-magenta": theme.colors.magenta,
  "--hlc-magenta-light": theme.colors.magentaLight,
  "--hlc-gold": theme.colors.gold,
  "--hlc-gold-deep": theme.colors.goldDeep,
  "--hlc-white": theme.colors.white,
  "--hlc-text": theme.colors.textPrimary,
  "--hlc-text-secondary": theme.colors.textSecondary,
  "--hlc-text-faint": theme.colors.textFaint,
  "--hlc-border": theme.colors.border,
  "--hlc-border-strong": theme.colors.borderStrong,
  "--hlc-success": theme.colors.success,
  "--hlc-gradient-bg": theme.gradients.background,
  "--hlc-gradient-primary": theme.gradients.primary,
  "--hlc-gradient-savings": theme.gradients.savings,
  "--hlc-gradient-glow": theme.gradients.glow,
  "--hlc-shadow-card": theme.shadows.card,
  "--hlc-shadow-glow": theme.shadows.glow,
  "--hlc-shadow-strong-glow": theme.shadows.strongGlow,
  "--hlc-blur": theme.effects.blur,
  "--hlc-radius": theme.effects.borderRadius,
  "--hlc-radius-sm": theme.effects.borderRadiusSm,
};

export default theme;
